import type { ReactNode } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { briefingStatusLabel } from "./briefings-table/BriefingStatusPill";
import {
  type BriefingsTableStatus,
  IS_NEEDS_REVIEW_ENABLED,
} from "./briefings-table/briefingsTable.types";

export type BriefingsStatusFilter = "all" | BriefingsTableStatus;

/** Tab order after "All"; values and labels come from the shared status type. */
const STATUS_TAB_ORDER = [
  "NEEDS_REVIEW",
  "ACTIVE",
  "SCHEDULED",
  "PAUSED",
] as const satisfies readonly BriefingsTableStatus[];

const STATUS_TABS: { value: BriefingsStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  ...STATUS_TAB_ORDER.filter(
    (status) => IS_NEEDS_REVIEW_ENABLED || status !== "NEEDS_REVIEW",
  ).map((status) => ({
    value: status,
    label: briefingStatusLabel(status),
  })),
];

interface BriefingsIndexContentSectionProps {
  children: ReactNode;
  activeStatus: BriefingsStatusFilter;
  onStatusChange: (status: BriefingsStatusFilter) => void;
  statusCounts: Record<BriefingsStatusFilter, number>;
  showStatus: boolean;
}

const BriefingsIndexContentSection = ({
  children,
  activeStatus,
  onStatusChange,
  statusCounts,
  showStatus,
}: BriefingsIndexContentSectionProps) => (
  <section className="flex min-h-0 w-full min-w-0 flex-1 flex-col bg-gray-50 px-4 pt-6 pb-8 sm:px-8 lg:px-16">
    {showStatus ? (
      <Tabs
        value={activeStatus}
        onValueChange={(value) => {
          const status = STATUS_TABS.find((tab) => tab.value === value)?.value;
          if (status) onStatusChange(status);
        }}
        className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col"
      >
        <div className="w-full min-w-0 max-w-full overflow-x-auto pb-1">
          <TabsList size="sm" className="w-fit">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} size="sm" value={tab.value}>
                <span className="inline-flex items-center gap-1.5">
                  <span>{tab.label}</span>
                  <span className="tabular-nums">
                    {statusCounts[tab.value]}
                  </span>
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent
          value={activeStatus}
          size="sm"
          className="mt-6 flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col"
        >
          {children}
        </TabsContent>
      </Tabs>
    ) : (
      children
    )}
  </section>
);

export default BriefingsIndexContentSection;
